import { useState, type ReactNode } from 'react';
import {
  Box,
  Card,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  useTheme,
  type Theme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslate } from 'react-admin';

/**
 * One column of a `CustomDataGrid`. `label` and every rendered value arrive
 * already translated — the grid itself never resolves a domain i18n key.
 */
export interface DataGridColumn<T> {
  /** Stable identifier — used as the cell key and as the mobile field key. */
  key: string;
  /** Already-translated column header / mobile field label. */
  label: string;
  /** Cell content for one row (text, `StatusChip`, any node). */
  render: (item: T) => ReactNode;
  /** Right-align the column (numerics/amounts). */
  align?: 'right';
  /**
   * Monospace treatment (`theme.custom.fontFamilyMono`): `'code'` is the
   * compact identifier/code style (0.75rem), `'value'` keeps the regular
   * cell size (amounts).
   */
  mono?: 'code' | 'value';
  /** Keep the cell on a single line (timestamps). */
  nowrap?: boolean;
  /** Server-side sort toggle rendered as a `TableSortLabel` on desktop. */
  sortable?: {
    active: boolean;
    direction: 'asc' | 'desc';
    onToggle: () => void;
  };
  /** Force this field into the collapsed section of the mobile card. */
  hideOnMobileCollapsed?: boolean;
  /**
   * Pull this field into the always-visible section of the mobile card, ahead
   * of unflagged columns — for the fields a phone user scans first (status,
   * amount). Ignored when `hideOnMobileCollapsed` is set.
   */
  mobileVisible?: boolean;
}

/** Server-side pagination block — mirrors the API's `page` metadata (1-based). */
export interface DataGridPagination {
  /** 1-based current page, straight from the API response. */
  page: number;
  pageSize: number;
  totalItems: number;
  /** Already-translated "rows per page" label. */
  rowsPerPageLabel: string;
  /** Receives the new 1-based page. */
  onPageChange: (page: number) => void;
  /** Receives the new page size; callers reset to page 1 themselves. */
  onPageSizeChange: (pageSize: number) => void;
}

/**
 * One pinned summary/totals row, rendered after the data rows regardless of
 * how the caller sorts them, with a visually distinct treatment (bold on the
 * header background). `cells` maps column keys to already-rendered content;
 * columns without an entry render an empty cell.
 */
export interface DataGridSummaryRow {
  /** Stable identifier of the summary row. */
  key: string;
  cells: Readonly<Partial<Record<string, ReactNode>>>;
}

export interface CustomDataGridProps<T> {
  columns: ReadonlyArray<DataGridColumn<T>>;
  rows: readonly T[];
  getRowKey: (item: T) => string;
  /** Navigate/select on row (desktop) or card body (mobile) activation. */
  onRowClick?: (item: T) => void;
  /** Already-translated `aria-label` applied to every row/card. */
  rowAriaLabel?: string;
  /** Highlight a row/card as the current selection. */
  isRowSelected?: (item: T) => boolean;
  /** Optional block rendered inside the card, above the table (e.g. a title). */
  header?: ReactNode;
  /** Totals rows pinned after the data rows — the caller's sorting never moves them. */
  summaryRows?: readonly DataGridSummaryRow[];
  /** Omit for short, unpaginated lists. */
  pagination?: DataGridPagination;
  /**
   * Minimum desktop table width (px). Below it the table keeps its readable
   * column widths and scrolls horizontally inside the card's scroll container
   * instead of crushing the columns. Ignored on mobile (cards, not a table).
   */
  tableMinWidth?: number;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

/** Fields (after the title) shown on a mobile card before expanding. */
const MOBILE_VISIBLE_FIELDS = 3;

const headCellSx = (theme: Theme) => ({
  ...theme.custom.table.headCell,
  backgroundColor: theme.custom.table.headerBg,
  borderBottom: `1px solid ${theme.custom.borderStrong}`,
});

function cellSx(column: Pick<DataGridColumn<unknown>, 'mono' | 'nowrap'>) {
  return (theme: Theme) => ({
    ...(column.mono && { fontFamily: theme.custom.fontFamilyMono }),
    ...(column.mono === 'code' && { fontSize: '0.75rem' }),
    ...(column.nowrap && { whiteSpace: 'nowrap' as const }),
  });
}

function PaginationBar({ pagination }: { pagination: DataGridPagination }) {
  const translate = useTranslate();
  return (
    <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
      <TablePagination
        component="div"
        count={pagination.totalItems}
        page={pagination.page - 1}
        rowsPerPage={pagination.pageSize}
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        labelRowsPerPage={pagination.rowsPerPageLabel}
        labelDisplayedRows={({ from, to, count }) =>
          translate('pay.dataGrid.displayedRows', { from, to, count })
        }
        onPageChange={(_event, newPage) => pagination.onPageChange(newPage + 1)}
        onRowsPerPageChange={(event) => pagination.onPageSizeChange(Number(event.target.value))}
      />
    </Box>
  );
}

function DesktopDataGrid<T>({
  columns,
  rows,
  getRowKey,
  onRowClick,
  rowAriaLabel,
  isRowSelected,
  header,
  summaryRows,
  pagination,
  tableMinWidth,
}: CustomDataGridProps<T>) {
  return (
    <Card sx={{ overflow: 'hidden' }}>
      {header}
      {/* Explicit horizontal scroll container: a wide table must scroll
          inside the card, never expand the card (and with it the page's
          scroll region) past the content column's padded width. */}
      <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
        <Table size="small" sx={tableMinWidth ? { minWidth: tableMinWidth } : undefined}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key} align={column.align} sx={headCellSx}>
                  {column.sortable ? (
                    <TableSortLabel
                      active={column.sortable.active}
                      direction={column.sortable.direction}
                      onClick={column.sortable.onToggle}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((item) => (
              <TableRow
                key={getRowKey(item)}
                hover
                selected={isRowSelected?.(item) ?? false}
                onClick={onRowClick ? () => onRowClick(item) : undefined}
                sx={onRowClick ? { cursor: 'pointer' } : undefined}
                aria-label={rowAriaLabel}
              >
                {columns.map((column) => (
                  <TableCell key={column.key} align={column.align} sx={cellSx(column)}>
                    {column.render(item)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {summaryRows?.map((summaryRow, index) => (
              <TableRow key={summaryRow.key}>
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.align}
                    sx={(theme) => ({
                      ...cellSx(column)(theme),
                      fontWeight: 600,
                      backgroundColor: theme.custom.table.headerBg,
                      ...(index === 0 && { borderTop: `2px solid ${theme.custom.borderStrong}` }),
                    })}
                  >
                    {summaryRow.cells[column.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && <PaginationBar pagination={pagination} />}
    </Card>
  );
}

function MobileFieldRow<T>({ column, item }: { column: DataGridColumn<T>; item: T }) {
  return (
    <Stack spacing={0.25}>
      <Typography
        component="span"
        sx={(theme) => ({
          fontSize: '0.75rem',
          fontWeight: 600,
          color: theme.custom.text.label,
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
          lineHeight: 1.2,
        })}
      >
        {column.label}
      </Typography>
      <Typography component="div" variant="body2" sx={cellSx(column)}>
        {column.render(item)}
      </Typography>
    </Stack>
  );
}

function MobileDataGrid<T>({
  columns,
  rows,
  getRowKey,
  onRowClick,
  rowAriaLabel,
  isRowSelected,
  header,
  summaryRows,
  pagination,
}: CustomDataGridProps<T>) {
  const translate = useTranslate();
  const [expandedKeys, setExpandedKeys] = useState<ReadonlySet<string>>(new Set());

  const [titleColumn, ...fieldColumns] = columns;
  if (!titleColumn) return null;
  // `mobileVisible` columns claim the visible slots first (a phone user scans
  // status/amount before anything else); unflagged columns fill what remains.
  const orderedFieldColumns = [
    ...fieldColumns.filter((column) => column.mobileVisible && !column.hideOnMobileCollapsed),
    ...fieldColumns.filter((column) => !column.mobileVisible || column.hideOnMobileCollapsed),
  ];
  const visibleColumns: Array<DataGridColumn<T>> = [];
  const collapsedColumns: Array<DataGridColumn<T>> = [];
  for (const column of orderedFieldColumns) {
    if (!column.hideOnMobileCollapsed && visibleColumns.length < MOBILE_VISIBLE_FIELDS) {
      visibleColumns.push(column);
    } else {
      collapsedColumns.push(column);
    }
  }

  const toggleExpanded = (key: string) => {
    setExpandedKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <Stack spacing={1.5}>
      {header}
      {rows.map((item) => {
        const key = getRowKey(item);
        const expanded = expandedKeys.has(key);
        return (
          <Card
            key={key}
            onClick={onRowClick ? () => onRowClick(item) : undefined}
            aria-label={rowAriaLabel}
            sx={(theme) => ({
              overflow: 'hidden',
              ...(onRowClick && { cursor: 'pointer' }),
              ...((isRowSelected?.(item) ?? false) && { backgroundColor: theme.palette.action.selected }),
            })}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={(theme) => ({
                px: 2,
                py: 1.25,
                backgroundColor: theme.custom.table.headerBg,
                borderBottom: `1px solid ${theme.palette.divider}`,
              })}
            >
              <Typography component="div" sx={{ fontWeight: 600, fontSize: '0.9375rem', minWidth: 0 }}>
                {titleColumn.render(item)}
              </Typography>
              {collapsedColumns.length > 0 && (
                <IconButton
                  size="small"
                  aria-expanded={expanded}
                  aria-label={translate(expanded ? 'pay.dataGrid.showLess' : 'pay.dataGrid.showMore')}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleExpanded(key);
                  }}
                  sx={{
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: (theme) =>
                      theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }),
                  }}
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
            <Stack spacing={1.5} sx={{ p: 2 }}>
              {visibleColumns.map((column) => (
                <MobileFieldRow key={column.key} column={column} item={item} />
              ))}
              {collapsedColumns.length > 0 && (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  <Stack
                    spacing={1.5}
                    sx={(theme) => ({ pt: 1.5, borderTop: `1px solid ${theme.palette.divider}` })}
                  >
                    {collapsedColumns.map((column) => (
                      <MobileFieldRow key={column.key} column={column} item={item} />
                    ))}
                  </Stack>
                </Collapse>
              )}
            </Stack>
          </Card>
        );
      })}
      {summaryRows?.map((summaryRow) => (
        <Card
          key={summaryRow.key}
          sx={(theme) => ({ overflow: 'hidden', backgroundColor: theme.custom.table.headerBg })}
        >
          <Stack spacing={1.5} sx={{ p: 2 }}>
            <Typography component="div" sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {summaryRow.cells[titleColumn.key]}
            </Typography>
            {fieldColumns
              .filter((column) => summaryRow.cells[column.key] !== undefined)
              .map((column) => (
                <Stack key={column.key} spacing={0.25}>
                  <Typography
                    component="span"
                    sx={(theme) => ({
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: theme.custom.text.label,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em',
                      lineHeight: 1.2,
                    })}
                  >
                    {column.label}
                  </Typography>
                  <Typography component="div" variant="body2" sx={[cellSx(column), { fontWeight: 600 }]}>
                    {summaryRow.cells[column.key]}
                  </Typography>
                </Stack>
              ))}
          </Stack>
        </Card>
      ))}
      {pagination && (
        <Card sx={{ overflow: 'hidden' }}>
          <PaginationBar pagination={pagination} />
        </Card>
      )}
    </Stack>
  );
}

/**
 * Shared responsive datagrid for every admin list screen. Props-driven
 * (server-side pagination via the caller's `filters` object + `useApiResource`)
 * rather than react-admin-list-context-driven, because these screens call the
 * typed `payApiClient` directly.
 *
 * Desktop (`>= sm`): the v3.4 unified datagrid — `custom.table.headCell`
 * header on `custom.table.headerBg` with a `borderStrong` bottom line, hover
 * rows, pointer cursor when clickable — wrapped in the standard white card.
 *
 * Mobile (`< sm`, the same split `CustomLayout` uses): each row becomes a
 * card — the first column's value is the card title, the next few fields show
 * as label/value rows, the rest sit behind an expand chevron. Tapping the
 * card activates `onRowClick`; the chevron only expands.
 *
 * Loading / error / empty states stay in the calling views (`LoadingState`,
 * `ErrorState`, `AccessDenied`, `EmptyState`) — the grid only ever renders
 * with data in hand.
 */
export function CustomDataGrid<T>(props: CustomDataGridProps<T>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return isMobile ? <MobileDataGrid {...props} /> : <DesktopDataGrid {...props} />;
}
